import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import RestaurantSectionCard from "../RestaurantSectionCard";
import { HEATMAP_HOURS, WEEKDAY_KEYS } from "../../data/restaurantDemoData";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";
import type { RestaurantMetrics } from "../../utils/restaurantMetrics";

const WEEKDAY_LABEL_KEYS: Record<(typeof WEEKDAY_KEYS)[number], string> = {
  sun: "restaurantAnalytics.weekdays.sun",
  mon: "restaurantAnalytics.weekdays.mon",
  tue: "restaurantAnalytics.weekdays.tue",
  wed: "restaurantAnalytics.weekdays.wed",
  thu: "restaurantAnalytics.weekdays.thu",
  fri: "restaurantAnalytics.weekdays.fri",
  sat: "restaurantAnalytics.weekdays.sat",
};

interface RestaurantOrderDensityHeatmapProps {
  metrics: RestaurantMetrics;
}

const RestaurantOrderDensityHeatmap: FC<RestaurantOrderDensityHeatmapProps> = ({ metrics }) => {
  const { t } = useTranslation();
  const fmt = useRestaurantFormatters();

  const maxOrders = useMemo(
    () => metrics.heatmap.reduce((max, cell) => Math.max(max, cell.orders), 1),
    [metrics.heatmap],
  );

  const cellAt = (weekday: number, hour: number) =>
    metrics.heatmap.find(cell => cell.weekday === weekday && cell.hour === hour);

  const formatHour = (hour: number) =>
    `${fmt.formatNumber(hour).padStart(2, fmt.formatNumber(0))}:${fmt.formatNumber(0).repeat(2)}`;

  return (
    <RestaurantSectionCard
      title={t("restaurantAnalytics.charts.orderDensity", { defaultValue: "Order density by day & hour" })}
      tip={t("restaurantAnalytics.chartTips.orderDensity", { defaultValue: "Order counts by weekday and hour — darker cells mark busier windows." })}
      bodyClassName="restaurant-heatmap-body"
    >
      <div className="restaurant-heatmap">
        <div className="restaurant-heatmap__row restaurant-heatmap__row--header">
          <span className="restaurant-heatmap__row-label" />
          {HEATMAP_HOURS.map(hour => (
            <span key={hour} className="restaurant-heatmap__hour-label">
              {formatHour(hour)}
            </span>
          ))}
        </div>
        {WEEKDAY_KEYS.map((key, weekday) => (
          <div key={key} className="restaurant-heatmap__row">
            <span className="restaurant-heatmap__row-label">{t(WEEKDAY_LABEL_KEYS[key])}</span>
            {HEATMAP_HOURS.map(hour => {
              const cell = cellAt(weekday, hour);
              const orders = cell?.orders ?? 0;
              const intensity = orders / maxOrders;
              return (
                <span
                  key={hour}
                  className="restaurant-heatmap__cell"
                  title={`${t(WEEKDAY_LABEL_KEYS[key])} ${formatHour(hour)} — ${fmt.formatNumber(orders)}`}
                  style={{
                    backgroundColor: `color-mix(in srgb, #2c68ad ${Math.round(12 + intensity * 88)}%, #eef1f5)`,
                    color: intensity > 0.62 ? "#ffffff" : "rgba(26, 26, 26, 0.72)",
                  }}
                >
                  {fmt.formatNumber(orders)}
                </span>
              );
            })}
          </div>
        ))}
        <div className="restaurant-heatmap__legend">
          <span>{fmt.formatNumber(0)}</span>
          <span className="restaurant-heatmap__legend-bar" />
          <span>{fmt.formatNumber(maxOrders)}</span>
        </div>
      </div>
    </RestaurantSectionCard>
  );
};

export default RestaurantOrderDensityHeatmap;
